import { useState } from 'react'
import { NavArrowLeft, Plus } from 'iconoir-react'
import { BACKGROUND_PRESETS, DEFAULT_APPEARANCE } from 'app/lib/forms/appearance'
import { FORM_CATEGORY_LABELS, QUESTION_KIND_LABELS } from 'app/lib/forms/builder'
import type { FormCategory, QuestionKind } from 'app/lib/forms/types'
import { PacketRunner } from 'app/components/form-runtime/PacketRunner'
import { PageScroll } from 'app/components/layout/PageScroll'
import Spinner from 'app/components/Spinner'
import { Badge } from 'app/components/ui/badge'
import { Button } from 'app/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu'
import { QUESTION_BLOCKS } from 'app/lib/forms/questionBlocks'
import { Input } from 'app/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { Textarea } from 'app/components/ui/textarea'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { Field } from 'app/components/workflows/inspectors/fields'
import { FormStatusBadge } from './FormStatusBadge'
import { EditorModeToggle, PreviewPane } from './EditorPreview'
import { QuestionCard } from './QuestionCard'
import type { SaveState, UseFormEditorReturn } from './useFormEditor'

const SAVE_STATE_TEXT: Record<SaveState, string> = {
  saved: `All changes saved`,
  saving: `Saving…`,
  failed: `Couldn't save. Your next edit tries again.`,
  needsName: `Name the form to save it`,
}

export function FormEditor(props: UseFormEditorReturn) {
  const { row, versions, fetching, notFound, readOnly, draft, saveState, busy, problems, preview, expandedId } = props
  const [previewing, setPreviewing] = useState(false)

  if (notFound) {
    return (
      <TableEmptyState
        title="Form not found"
        description="It may have been deleted."
        action={
          <Button variant="white" size="sm" onClick={props.back}>
            Back to forms
          </Button>
        }
      />
    )
  }
  const loading = fetching || !row || !draft || !preview
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  const live = versions?.published
  const newerDraft = versions && versions.latest.id !== row.id ? versions.latest : undefined
  const addMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="dashed" className="w-full" disabled={readOnly}>
          <Plus /> Add question
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {(Object.keys(QUESTION_KIND_LABELS) as QuestionKind[]).map((kind) => (
          <DropdownMenuItem key={kind} onSelect={() => props.addQuestion(kind)}>
            {QUESTION_KIND_LABELS[kind]}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Ready-made blocks</DropdownMenuLabel>
        {QUESTION_BLOCKS.map((block) => (
          <DropdownMenuItem key={block.key} onSelect={() => props.addBlock(block)}>
            <span className="flex flex-col">
              <span>{block.name}</span>
              <span className="text-xs text-subtle">{block.description}</span>
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  const header = (
    <div className="flex flex-col gap-3 pb-4">
      <Button variant="subtle" size="inline" className="self-start" onClick={props.back}>
        <NavArrowLeft /> Forms
      </Button>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          aria-label="Form name"
          className="h-10 max-w-md flex-1 text-lg font-semibold"
          value={draft.name}
          disabled={readOnly}
          onChange={(e) => props.setField(`name`, e.target.value)}
        />
        {versions && <FormStatusBadge entry={versions} />}
        {readOnly && <Badge label={`Viewing v${row.formVersion}`} />}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <EditorModeToggle previewing={previewing} onChange={setPreviewing} />
          {!readOnly && <span className="text-xs text-subtle">{SAVE_STATE_TEXT[saveState]}</span>}
          {readOnly ? (
            newerDraft ? (
              <Button variant="primary" size="sm" onClick={() => props.openVersion(newerDraft.id)}>
                {`Open draft v${newerDraft.formVersion}`}
              </Button>
            ) : (
              <Button variant="primary" size="sm" disabled={busy} onClick={props.editNewVersion}>
                Edit as new version
              </Button>
            )
          ) : (
            <>
              {live && (
                <Button variant="subtle" size="sm" onClick={() => props.openVersion(live.id)}>
                  {`View live v${live.formVersion}`}
                </Button>
              )}
              <Button variant="primary" size="sm" disabled={busy || problems.length > 0} onClick={props.publish}>
                Publish
              </Button>
            </>
          )}
        </div>
      </div>
      {readOnly && (
        <p className="text-sm text-subtle">
          Published versions never change, so every signed copy matches what the patient saw. Edit as a new version to
          make changes.
        </p>
      )}
    </div>
  )

  return (
    <PageScroll header={header}>
      {previewing ? (
        <PreviewPane background={BACKGROUND_PRESETS[DEFAULT_APPEARANCE.backgroundPreset]}>
          <PacketRunner
            packet={{
              name: preview.name,
              appearance: DEFAULT_APPEARANCE,
              branding: { displayName: `Your practice` },
              forms: [preview],
            }}
            onSubmitForm={async () => undefined}
            onComplete={() => undefined}
          />
        </PreviewPane>
      ) : (
        <div className="flex max-w-3xl flex-col gap-4 pb-10">
          <fieldset disabled={readOnly} className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Category"
              info="Sets the document category when the finished form is filed in the patient chart."
            >
              <Select
                value={draft.category}
                onValueChange={(category) => props.setField(`category`, category as FormCategory)}
              >
                <SelectTrigger aria-label="Category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(FORM_CATEGORY_LABELS) as FormCategory[]).map((category) => (
                    <SelectItem key={category} value={category}>
                      {FORM_CATEGORY_LABELS[category]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Description">
              <Textarea
                rows={1}
                placeholder="For staff. Patients don't see this."
                value={draft.description}
                onChange={(e) => props.setField(`description`, e.target.value)}
              />
            </Field>
          </fieldset>

          <div className="flex flex-col gap-2">
            {draft.questions.map((question, index) => (
              <QuestionCard
                key={question.id}
                question={question}
                index={index}
                questions={draft.questions}
                expanded={expandedId === question.id}
                readOnly={readOnly}
                onToggle={() => props.setExpandedId(expandedId === question.id ? undefined : question.id)}
                onChange={(next) => props.updateQuestion(question.id, next)}
                onMove={(to) => props.moveQuestion(index, to)}
                onReorder={props.moveQuestion}
                onDuplicate={() => props.duplicateQuestion(question.id)}
                onRemove={() => props.removeQuestion(question.id)}
              />
            ))}
          </div>
          {!readOnly && addMenu}

          {!readOnly && problems.length > 0 && (
            <div className="rounded-lg border border-default bg-surface-muted px-4 py-3 text-sm">
              <p className="font-medium text-strong">Before you can publish</p>
              <ul className="mt-1 list-disc pl-5 text-subtle">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </PageScroll>
  )
}
